import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  useStartLinkedTasks,
  useTaskCommands,
  type CommandOptions,
  type CommandResult,
  type TaskChoice,
} from '@/features/tasks';
import { HeldActionsContext, type HoldRequest } from '../heldActionsContext';
import {
  HOLD_MS,
  NO_HELD_ACTIONS,
  actionFor,
  expire,
  prune,
  settle,
  take,
  undo as undoAction,
  type HeldActions,
} from '../heldActions';
import type { QueueRow } from '../yourTurn';

/**
 * Holds every action taken in Your turn for six seconds, then sends it
 * (spec 154 US2, research R1, R3, R8).
 *
 * - Nothing reaches the service while an action is held; Undo removes it.
 * - When the time is up the action is sent once, through the tasks
 *   feature's own commands, so its toasts and refusals read as anywhere else.
 * - Leaving the Overview sends what is held at once; so does the page going
 *   away, with `keepalive`, so an action outlives the tab it was taken in.
 * - Holding and undoing are said once, in a polite live region. Sending and
 *   refusals are said by the commands' toasts.
 */

interface Pending {
  timer: ReturnType<typeof setTimeout>;
  send: (options: CommandOptions) => void;
}

let nextId = 0;

export function HeldActionsProvider({
  workspaceId,
  present,
  children,
}: {
  workspaceId: string;
  /** The tasks in the queue now: a sent action is forgotten once its leave. */
  present: ReadonlySet<string>;
  children: ReactNode;
}) {
  const { report } = useTaskCommands(workspaceId);
  const { reportStartAll } = useStartLinkedTasks(workspaceId);
  const [state, setState] = useState<HeldActions>(NO_HELD_ACTIONS);
  const [said, setSaid] = useState('');
  const pending = useRef(new Map<string, Pending>());
  // Read when an action is taken, between renders.
  const latest = useRef(state);
  useLayoutEffect(() => {
    latest.current = state;
  }, [state]);

  // A sent action is forgotten once its tasks have left the queue, so a task
  // that comes back later (at its next gate) starts clean. Adjusted while
  // rendering, when the queue changes, rather than in an effect.
  const [seen, setSeen] = useState(present);
  if (seen !== present) {
    setSeen(present);
    setState((current) => prune(current, present));
  }

  const sendOne = useCallback(
    (
      choice: TaskChoice,
      row: QueueRow,
      options: CommandOptions,
    ): Promise<CommandResult> => {
      switch (choice.kind) {
        case 'decide':
          return report.decide(
            row.task,
            choice.outcome,
            choice.targetLabel,
            undefined,
            options,
          );
        case 'run-again':
          return report.runAgain(row.task, options);
        case 'start-subtasks':
          return reportStartAll(row.task, options);
      }
    },
    [report, reportStartAll],
  );

  const fire = useCallback((id: string, options: CommandOptions) => {
    const entry = pending.current.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.current.delete(id);
    setState((current) => expire(current, id));
    entry.send(options);
  }, []);

  const hold = useCallback(
    ({ choice, rows, label, subject }: HoldRequest): boolean => {
      const taskIds = rows.map((row) => row.taskId);
      if (taskIds.some((taskId) => actionFor(latest.current, taskId))) {
        return false;
      }
      nextId += 1;
      const id = `held-${String(nextId)}`;
      const kind = rows.length > 1 ? 'batch' : choice.kind;
      const next = take(
        latest.current,
        { id, kind, taskIds, label },
        Date.now(),
      );
      latest.current = next;
      setState(next);

      const send = (options: CommandOptions) => {
        // One after another: a batch's moves are separate requests, and each
        // answers for its own task.
        void rows
          .reduce<Promise<Record<string, CommandResult>>>(
            (done, row) =>
              done.then(async (outcomes) => ({
                ...outcomes,
                [row.taskId]: await sendOne(choice, row, options),
              })),
            Promise.resolve({}),
          )
          .then((outcomes) => {
            setState((current) => settle(current, id, outcomes));
          });
      };
      pending.current.set(id, {
        timer: setTimeout(() => {
          fire(id, {});
        }, HOLD_MS),
        send,
      });
      setSaid(
        `${label} for ${subject} in ${String(HOLD_MS / 1000)} seconds. Undo is available.`,
      );
      return true;
    },
    [fire, sendOne],
  );

  const undo = useCallback((id: string) => {
    const entry = pending.current.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.current.delete(id);
    const action = latest.current.actions.find((each) => each.id === id);
    setState((current) => undoAction(current, id));
    if (action) setSaid(`${action.label} undone.`);
  }, []);

  // Whatever is held when the page goes away is sent then, not lost (FR-009).
  useEffect(() => {
    const map = pending.current;
    const sendAll = (options: CommandOptions) => {
      for (const id of [...map.keys()]) fire(id, options);
    };
    const onPageHide = () => {
      sendAll({ keepalive: true });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      sendAll({});
    };
  }, [fire]);

  const api = useMemo(() => ({ state, hold, undo }), [state, hold, undo]);

  return (
    <HeldActionsContext.Provider value={api}>
      {children}
      <p role="status" className="sr-only">
        {said}
      </p>
    </HeldActionsContext.Provider>
  );
}
